import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import customerAPI from "../../api/customerApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { useLoader } from "../../utils/LoaderUtils";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";

function List() {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const { showLoader, hideLoader } = useLoader();

  const query = useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 50,
      status: searchParams.get("status") || "",
      search: searchParams.get("search") || "",
    }),
    [searchParams],
  );

  const fetchCustomers = useCallback(async () => {
    showLoader();

    try {
      const res = await customerAPI.getDetails(query);

      if (res.code === 200) {
        setItems(res.data.customers);
        setPagination(res.data.pagination);
      }
    } catch (err) {
    } finally {
      hideLoader();
    }
  }, [query, hideLoader, showLoader]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const updateFilter = useCallback((key, value) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) params.set(key, value);
      else params.delete(key);
      return params;
    });
  }, [setSearchParams]);

  const handleStatusChange = useCallback(
    async (customerId, status) => {
      showLoader();

      const res = await customerAPI.updateStatus({ customerId, status });

      hideLoader();

      if (res.code === 200) {
        showSuccess("Status updated successfully");
        fetchCustomers();
      } else {
        showError(res.message || "Failed to update status");
      }
    },
    [fetchCustomers, hideLoader, showLoader],
  );

  const startIndex = useMemo(() => {
    return (query.page - 1) * query.limit;
  }, [query]);

  return (
    <div className="content">
      <div className="page-header">
        <div className="add-item d-flex">
          <div className="page-title">
            <h4>All Customers</h4>
            <h6>Keep track and manage your customer database efficiently</h6>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text" id="basic-addon1">
                  <i className="ti ti-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search By Store Name/Email Address/Phone Number"
                  value={searchParams.get("search") || ""}
                  onChange={(e) => updateFilter("search", e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6" style={{ justifyItems: "right" }}>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center row-gap-3">
                <select
                  className="form-control ms-2"
                  value={searchParams.get("status") || ""}
                  onChange={(e) => updateFilter("status", e.target.value)}
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="row">
            <div className="col-md-12">
              <div className="countablepagination p-2">
                <p>
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{" "}
                  of {pagination.total} results
                </p>
              </div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead className="thead-light">
                <tr>
                  <th>S. No. </th>
                  <th>Customer Details</th>
                  <th>Contact Details</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <tr key={item._id}>
                      <td>{startIndex + index + 1}</td>

                      <td>
                        <Link to={`/customer/details/${item._id}`}>
                          {item.firstName} {item.lastName}
                        </Link>
                      </td>

                      <td>
                        {item.email && (
                          <div>
                            <a href={`mailto:${item.email}`}>{item.email}</a>
                          </div>
                        )}
                        {item.phone && (
                          <div>
                            <a href={`tel:${item.phone}`}>+91-{item.phone}</a>
                          </div>
                        )}
                      </td>

                      <td>
                        <span
                          className={`badge ${
                            item.status === "active"
                              ? "badge-success"
                              : "badge-danger"
                          }`}
                        >
                          {item.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td>
                        <button
                          className={`btn btn-sm ${
                            item.status === "active"
                              ? "btn-danger"
                              : "btn-primary"
                          }`}
                          onClick={() =>
                            handleStatusChange(
                              item._id,
                              item.status === "active" ? "deactive" : "active",
                            )
                          }
                        >
                          {item.status === "active" ? "Deactivate" : "Activate"}
                        </button>

                        <Link
                          to={`/customer/details/${item._id}`}
                          className="btn btn-primary btn-sm ms-2"
                        >
                          View Orders
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center p-4 text-muted">
                      No customers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="row">
            <RowsPerPage
              value={searchParams.get("limit") || "10"}
              onChange={(val) => updateFilter("limit", val)}
            />

            <Pagination pagination={pagination} updateFilter={updateFilter} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;
